import { useEffect, useMemo, useState } from 'react';
import './Brief.css';

// ============================================================
// BRIEF — a read-only synopsis of the board. Zero writes, zero
// fetches: everything derives from the items Watch.js already holds,
// and the day math (parseDate / daysSince / isCold) is Watch.js's own,
// handed down as props so there is exactly one definition of "cold".
// ============================================================

const DUE_HORIZON_DAYS = 30;

// Counterparty → last name, or the org's domain for a bare address.
// "Alan de la Houssaye (CRC Group)" → "Houssaye"; "x@crcgroup.com" → "crcgroup".
function shortParty(s) {
  const raw = (s || '').trim();
  if (!raw) return '';
  if (raw.includes('@')) return raw.split('@')[1].split('.')[0];
  const base = raw.split(/[(,/|]/)[0].trim();
  const words = base.split(/\s+/).filter(Boolean);
  return words[words.length - 1] || base;
}

function Section({ label, count, children, collapsible }) {
  const [open, setOpen] = useState(false);
  const empty = count === 0;
  const toggle = collapsible && !empty;
  const shown = !empty && (!collapsible || open);
  return (
    <section className={'Brief-section' + (empty ? ' Brief-section--empty' : '')}>
      <div className={'Brief-section-header' + (toggle ? ' Brief-section-header--toggle' : '')}
        onClick={toggle ? () => setOpen(o => !o) : undefined}
        role={toggle ? 'button' : undefined} tabIndex={toggle ? 0 : undefined}
        aria-expanded={toggle ? open : undefined}
        onKeyDown={toggle ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(o => !o); } } : undefined}>
        <span className="Brief-section-title">{label}</span>
        <span className="Brief-chip">{empty ? '—' : count}</span>
        {toggle && <span className="Brief-section-caret">{open ? '▾' : '▸'}</span>}
      </div>
      {shown && <div className="Brief-rows">{children}</div>}
    </section>
  );
}

// D1.5: the synopsis lines under a title. `standing` alone, or with the
// move for WAITING ON rows. Nothing below CONFIDENCE_MIN — a low-confidence
// reading is not shown as if it were true.
const CONFIDENCE_MIN = 0.5;
export function statusLines(item, withMove = false) {
  const s = item?.synopsis;
  if (!s || !s.standing || typeof s.confidence !== 'number' || s.confidence < CONFIDENCE_MIN) return [];
  const lines = [s.standing];
  if (withMove && s.move) lines.push(s.move);
  return lines;
}

function Row({ item, meta, flag, onJump, sub = [] }) {
  return (
    <div className={'Brief-row' + (flag ? ` Brief-row--${flag}` : '')} onClick={() => onJump(item.id)}
      role="button" tabIndex={0}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onJump(item.id); } }}>
      <span className="Brief-row-id">#{item.id}</span>
      <span className="Brief-row-main">
        <span className="Brief-row-title" title={item.title}>{item.title}</span>
        {sub.map((line, i) => <span key={i} className="Brief-row-sub" title={line}>{line}</span>)}
      </span>
      <span className="Brief-row-meta">{meta}</span>
    </div>
  );
}

export default function Brief({ items, today, parseDate, daysSince, isCold, fmtD, onClose, onJump }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const model = useMemo(() => {
    const open = items.filter(i => i.status === 'Open');
    const horizon = new Date(today.getFullYear(), today.getMonth(), today.getDate() + DUE_HORIZON_DAYS);
    const dayDiff = (a, b) => Math.round((a - b) / 86400000);

    const due = open
      .map(i => ({ item: i, date: parseDate(i.dueDate) }))
      .filter(r => r.date && r.date <= horizon)
      .map(r => ({ ...r, delta: dayDiff(r.date, today) })) // <0 overdue, 0 today, >0 ahead
      .sort((a, b) => a.date - b.date || a.item.id - b.item.id);

    const awake = open
      .map(i => ({ item: i, date: parseDate(i.wakeDate) }))
      .filter(r => r.date && r.date <= today)
      .map(r => ({ ...r, ago: dayDiff(today, r.date) }))
      .sort((a, b) => a.date - b.date || a.item.id - b.item.id);

    const staleFirst = (a, b) => (b.days ?? -1) - (a.days ?? -1) || a.item.id - b.item.id;
    const withDays = (i) => ({ item: i, days: daysSince(i) });

    const waiting = open.filter(i => i.court === 'Theirs').map(withDays)
      .map(r => ({ ...r, cold: isCold(r.item) })).sort(staleFirst);
    const mine = open.filter(i => i.court === 'Mine').map(withDays).sort(staleFirst);
    const holding = open.filter(i => i.court === 'Holding').map(withDays).sort(staleFirst);
    const untriaged = open.filter(i => i.triaged === false).length;

    return { open: open.length, due, awake, waiting, mine, holding, untriaged };
  }, [items, today, parseDate, daysSince, isCold]);

  const dueMeta = (r) => {
    const d = fmtD(r.date);
    if (r.delta < 0) return `${d} · OVERDUE`;
    if (r.delta === 0) return `${d} · TODAY`;
    return `${d} · in ${r.delta}d`;
  };
  const nd = (days) => (days == null ? '' : `${days}d`);

  return (
    <div className="Brief-backdrop" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="Brief" role="dialog" aria-modal="true" aria-label="Board brief">
        <button className="Brief-close" title="Close" onClick={onClose}>×</button>
        <div className="Brief-masthead">BRIEF</div>

        <div className="Brief-strip">
          <span className="Brief-strip-cell"><b>{model.open}</b> OPEN</span>
          <span className="Brief-strip-dot">·</span>
          <span className="Brief-strip-cell"><b>{model.waiting.length}</b> WAITING</span>
          <span className="Brief-strip-dot">·</span>
          <span className="Brief-strip-cell"><b>{model.mine.length}</b> MINE</span>
          <span className="Brief-strip-dot">·</span>
          <span className="Brief-strip-cell"><b>{model.holding.length}</b> HOLDING</span>
          <span className="Brief-strip-dot">·</span>
          <span className={'Brief-strip-cell' + (model.untriaged > 0 ? ' Brief-strip-cell--accent' : '')}>
            <b>{model.untriaged}</b> UNTRIAGED
          </span>
        </div>

        <Section label="Due" count={model.due.length}>
          {model.due.map(r => (
            <Row key={r.item.id} item={r.item} meta={dueMeta(r)} flag={r.delta < 0 ? 'overdue' : null} onJump={onJump} sub={statusLines(r.item)} />
          ))}
        </Section>

        <Section label="Awake" count={model.awake.length}>
          {model.awake.map(r => (
            <Row key={r.item.id} item={r.item} meta={r.ago === 0 ? 'woke today' : `woke ${r.ago}d ago`} onJump={onJump} />
          ))}
        </Section>

        <Section label="Waiting on" count={model.waiting.length}>
          {model.waiting.map(r => {
            const who = shortParty(r.item.counterparty);
            return (
              <Row key={r.item.id} item={r.item} flag={r.cold ? 'cold' : null} onJump={onJump} sub={statusLines(r.item, true)}
                meta={<>
                  {who && <span className="Brief-row-party" title={r.item.counterparty}>{who}</span>}
                  {who && r.days != null && ' · '}
                  {r.cold ? `COLD ${nd(r.days)}` : nd(r.days)}
                </>} />
            );
          })}
        </Section>

        <Section label="My court" count={model.mine.length}>
          {model.mine.map(r => (
            <Row key={r.item.id} item={r.item} meta={nd(r.days)} onJump={onJump} sub={statusLines(r.item)} />
          ))}
        </Section>

        <Section label="Holding" count={model.holding.length} collapsible>
          {model.holding.map(r => (
            <Row key={r.item.id} item={r.item} meta={nd(r.days)} onJump={onJump} />
          ))}
        </Section>
      </div>
    </div>
  );
}
